import React, { useEffect, memo, useMemo, useRef, useState } from 'react';
import { Select, SelectItem, Chip } from '@heroui/react';
import { Info, Plus, Trash, X } from '@phosphor-icons/react';
import { useMassMailContext } from '../context/MassMailContext';
import { QuickCreateTemplateForm, QuickDeleteTemplateForm } from './QuickCreateTemplateModal';

export const TemplateManager: React.FC = memo(() => {
  const { state, dispatch } = useMassMailContext();
  const [activePanel, setActivePanel] = useState<'none' | 'create' | 'delete'>('none');

  const togglePanel = (panel: 'create' | 'delete') => {
    setActivePanel(prev => prev === panel ? 'none' : panel);
  };

  // Merge context templates with backend templates so both are always available.
  // When a helpdesk reply is opened, contextTemplates carries the Helpdesk
  // quick-replies but the user must still be able to pick any other category
  // (e.g. Verhuur) — so we always concatenate both sets here.
  const allTemplates = useMemo(() => {
    if (state.contextTemplates.length > 0) {
      return [...state.contextTemplates, ...state.templates];
    }
    return state.templates;
  }, [state.templates, state.contextTemplates]);

  // Build the type-dropdown list from every source we know about: the context
  // templates' types (default for the current flow), the backend types
  // endpoint, AND the actual `templateType` field on the loaded backend
  // templates. The last source is critical: if a category exists in the DB
  // (e.g. "Verhuur") but its row is missing from the /types endpoint for any
  // reason, the user would otherwise lose access to those templates when the
  // modal is opened with contextTemplates seeded.
  const allTemplateTypes = useMemo(() => {
    const sources = [
      ...state.contextTemplates.map(t => t.templateType),
      ...state.templateTypes.map(t => t.name),
      ...state.templates.map(t => t.templateType),
    ];
    const seen = new Set<string>();
    const merged: string[] = [];
    for (const name of sources) {
      if (name && !seen.has(name)) {
        seen.add(name);
        merged.push(name);
      }
    }
    return merged.map(name => ({ name }));
  }, [state.templates, state.templateTypes, state.contextTemplates]);

  // Auto-select template type if context templates are provided
  useEffect(() => {
    if (state.contextTemplates.length > 0 && !state.selectedType) {
      // Get the first context template's type
      const firstContextType = state.contextTemplates[0]?.templateType;
      if (firstContextType) {
        console.log('🎯 Auto-selecting template type:', firstContextType);
        dispatch({ type: 'SET_SELECTED_TYPE', payload: firstContextType });
      }
    }
  }, [state.contextTemplates, state.selectedType, dispatch]);

  // Auto-select the first context template when context templates are provided
  // and none is selected yet. Saves the user a click when entering the page
  // with a pre-loaded set of templates (e.g., incasso reminder flow).
  // Runs once: if the user later clears the template manually we respect that.
  // Opt out via `autoSelectFirstTemplate: false` (helpdesk reply editor starts blank).
  const didAutoSelectTemplateRef = useRef(false);
  useEffect(() => {
    if (didAutoSelectTemplateRef.current) return;
    if (state.autoSelectFirstTemplate === false) return;
    if (
      state.contextTemplates.length > 0 &&
      !state.selectedTemplate &&
      state.selectedType === state.contextTemplates[0]?.templateType
    ) {
      const firstTemplate = state.contextTemplates[0];
      const firstTemplateId = firstTemplate?.id?.toString();
      if (firstTemplateId) {
        console.log('🎯 Auto-selecting first context template:', firstTemplateId);
        didAutoSelectTemplateRef.current = true;
        dispatch({ type: 'SET_SELECTED_TEMPLATE', payload: firstTemplateId });
        dispatch({ type: 'SET_EMAIL_SUBJECT', payload: firstTemplate.subject });
        dispatch({ type: 'SET_EMAIL_CONTENT', payload: firstTemplate.content });
      }
    }
  }, [state.contextTemplates, state.selectedTemplate, state.selectedType, dispatch]);

  // Filter templates based on selected type
  useEffect(() => {
    console.log('🔍 Template filtering debug:', {
      selectedType: state.selectedType,
      totalDbTemplates: state.templates.length,
      totalContextTemplates: state.contextTemplates.length,
      totalAllTemplates: allTemplates.length,
      templateTypesInTemplates: allTemplates.map(t => t.templateType),
      uniqueTemplateTypes: [...new Set(allTemplates.map(t => t.templateType))]
    });

    if (state.selectedType) {
      const filtered = allTemplates.filter(
        template => template.templateType === state.selectedType
      );
      console.log('🎯 Filtered result:', {
        selectedType: state.selectedType,
        filteredCount: filtered.length,
        filteredTemplates: filtered.map(t => ({ 
          id: t.id, 
          label: t.label,
          subject: t.subject, 
          templateType: t.templateType 
        }))
      });
      dispatch({ type: 'SET_FILTERED_TEMPLATES', payload: filtered });
    } else {
      dispatch({ type: 'SET_FILTERED_TEMPLATES', payload: allTemplates });
    }
  }, [state.selectedType, allTemplates, dispatch]);

  const handleTypeChange = (value: string) => {
    console.log('🎯 Template type changed:', {
      newSelectedType: value,
      availableTypes: state.templateTypes.map(t => t.name)
    });
    dispatch({ type: 'SET_SELECTED_TYPE', payload: value });
    dispatch({ type: 'SET_SELECTED_TEMPLATE', payload: '' });
    dispatch({ type: 'SET_EMAIL_SUBJECT', payload: '' });
    dispatch({ type: 'SET_EMAIL_CONTENT', payload: '' });
  };

  const handleTemplateChange = (value: string) => {
    dispatch({ type: 'SET_SELECTED_TEMPLATE', payload: value });

    // Load template content - keep placeholders as variables!
    const selectedTemplate = state.filteredTemplates.find(
      template => template.id?.toString() === value
    );

    if (selectedTemplate) {
      // Don't replace placeholders here - they should stay as variables in the editor
      // They will be replaced only in preview and when sending
      dispatch({ type: 'SET_EMAIL_SUBJECT', payload: selectedTemplate.subject });
      dispatch({ type: 'SET_EMAIL_CONTENT', payload: selectedTemplate.content });
    }
  };

  // Clear the selected template so the user can compose plain-text content
  // without any template prefill. We also wipe subject + content so the editor
  // is a clean slate — otherwise the previously loaded template body lingers
  // and is misleading.
  const handleClearTemplate = () => {
    dispatch({ type: 'SET_SELECTED_TEMPLATE', payload: '' });
    dispatch({ type: 'SET_EMAIL_SUBJECT', payload: '' });
    dispatch({ type: 'SET_EMAIL_CONTENT', payload: '' });
  };


  return (
    <>
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Template</h3>
        <div className="flex items-center gap-2">
          {allTemplates.length > 0 && (
            <Chip size="sm" className="bg-blue-500/10 text-blue-700 border border-blue-500/30">
              {allTemplates.length} template{allTemplates.length !== 1 ? 's' : ''}
            </Chip>
          )}
          <button
            onClick={() => togglePanel('create')}
            className={`flex items-center gap-1 text-xs font-medium transition-colors ${activePanel === 'create' ? 'text-blue-800' : 'text-blue-600 hover:text-blue-800'}`}
            title="Nieuw template aanmaken"
          >
            <Plus size={13} weight="bold" />
            Nieuw
          </button>
          {state.templates.length > 0 && (
            <button
              onClick={() => togglePanel('delete')}
              className={`flex items-center gap-1 text-xs font-medium transition-colors ${activePanel === 'delete' ? 'text-red-800' : 'text-red-500 hover:text-red-700'}`}
              title="Template verwijderen"
            >
              <Trash size={13} weight="bold" />
              Verwijder
            </button>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {/* Template Type Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Template Type
          </label>
          <Select
            placeholder="Selecteer template type..."
            selectedKeys={state.selectedType ? [state.selectedType] : []}
            onSelectionChange={(keys) => {
              const selected = Array.from(keys)[0] as string;
              handleTypeChange(selected || '');
            }}
            size="sm"
          >
            {allTemplateTypes.map((type) => (
              <SelectItem key={type.name} textValue={type.name}>
                {type.name}
              </SelectItem>
            ))}
          </Select>
        </div>

        {/* Template Selection */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">
              Template
            </label>
            {state.selectedTemplate && (
              <button
                type="button"
                onClick={handleClearTemplate}
                className="inline-flex items-center gap-1 text-xs font-medium text-neutral-secondary-mid transition-colors hover:text-brick-primary"
                title="Selectie wissen om plain-text e-mail te versturen"
              >
                <X size={12} weight="bold" />
                Wis selectie
              </button>
            )}
          </div>
          <Select
            placeholder="Selecteer een template..."
            selectedKeys={state.selectedTemplate ? [state.selectedTemplate] : []}
            onSelectionChange={(keys) => {
              const selected = Array.from(keys)[0] as string;
              handleTemplateChange(selected || '');
            }}
            isDisabled={!state.selectedType || state.filteredTemplates.length === 0}
            size="sm"
          >
            {state.filteredTemplates.map((template) => {
              const name = template.subject || template.label || 'Naamloos template';
              const displayText = template.label ? `${name} (${template.label})` : name;
              const keyValue = String(template.id || `template-${Math.random()}`);
              return (
                <SelectItem
                  key={keyValue}
                  textValue={displayText}
                >
                  {displayText}
                </SelectItem>
              );
            })}
          </Select>
          
          {state.selectedType && state.filteredTemplates.length === 0 && (
            <p className="text-sm text-gray-500 mt-1">
              Geen templates gevonden voor dit type
            </p>
          )}

          {/* Show template description/side-effects when a template with a description is selected */}
          {state.selectedTemplate && (() => {
            const selected = state.filteredTemplates.find(
              t => t.id?.toString() === state.selectedTemplate
            );
            if (!selected?.description) return null;
            return (
              <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2">
                <Info size={16} weight="fill" className="mt-0.5 shrink-0 text-amber-600" />
                <p className="text-sm text-amber-800">{selected.description}</p>
              </div>
            );
          })()}
        </div>

      </div>
    </div>

    {activePanel === 'create' && (
      <QuickCreateTemplateForm
        onClose={() => setActivePanel('none')}
        isContextMode={state.contextTemplates.length > 0}
      />
    )}
    {activePanel === 'delete' && (
      <QuickDeleteTemplateForm
        onClose={() => setActivePanel('none')}
      />
    )}
    </>
  );
});
